import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import ReactPlayer from "react-player";
import { Helmet } from "react-helmet-async";
import styles from "./Homepage.module.scss";

/* ─── assets ─── */
import logo from "LandingPage/Assets/logo.png";

/* ─── icons (inline SVGs via JSX for security - no dangerouslySetInnerHTML) ─── */
const SearchIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChartIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const UsersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BrainIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a4 4 0 0 1 4 4c0 1.1-.5 2.1-1.3 2.8A5 5 0 0 1 17 13v1a3 3 0 0 1-3 3h-1v5h-2v-5H10a3 3 0 0 1-3-3v-1a5 5 0 0 1 2.3-4.2A4 4 0 0 1 12 2z" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PaletteIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
    <circle cx="6.5" cy="12" r="0.5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.38-.15-.73-.39-1.01-.23-.27-.37-.62-.37-1.01C12.74 17.15 13.82 16 15.5 16H17c2.76 0 5-2.24 5-5 0-4.42-4.03-9-10-9z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);



const PlayIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

/* ─── animation variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

/* ─── data ─── */
const EXPLORE_FEATURES = [
  /* PLACEHOLDER: Explore features */
  { icon: <SearchIcon />, text: "Advanced Search & Discovery" },
  { icon: <ChartIcon />, text: "Strategic Benchmarking & Impact" },
  { icon: <BrainIcon />, text: "AI Collaboration & Team Building" },
  { icon: <UsersIcon />, text: "Faculty Profiling & Management" },
];

const PUBLIC_PROFILE_FEATURES = [
  /* PLACEHOLDER: Public Profile features */
  { icon: <BuildingIcon />, text: "Institution Profiles & Analytics" },
  { icon: <UsersIcon />, text: "Faculty Showcase & Directory" },
  { icon: <GlobeIcon />, text: "Research Highlights & Publications" },
  { icon: <PaletteIcon />, text: "Custom Branding & Design" },
];

/* ─── main component ─── */
const Homepage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const heroRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      // scroll logic here if needed later
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const currentUrl = `${window.location.origin}${window.location.pathname}`;

  const generateTitle = () => {
    return "Academic Online | Explore Faculty, Research & Institutions";
  };

  const generateDescription = () => {
    return "Discover faculty profiles, publications, grants, and institution data across U.S. higher education. The academic intelligence platform for researchers and scholars.";
  };

  return (
    <main className={styles["homepage"]}>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>

      {/* ════════════════════════════════════════════════════════
          SECTION 1: HERO
          ════════════════════════════════════════════════════════ */}
      <section 
        className={styles["hero"]}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-background.webp)` }}
        ref={heroRef} 
        id="hero"
      >
        {/* Subtle overlay to keep text legible in modern-minimal style */}
        <div className={styles["hero__overlay"]} />
        
        <motion.div
          className={styles["hero__content"]}
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className={styles["hero__badge"]} variants={fadeUp}>
            <img src={logo} alt="Academic Online Logo" className={styles["hero__badge-logo"]} />
          </motion.div>

          {/* PLACEHOLDER: Hero headline */}
          <motion.h1 className={styles["hero__title"]} variants={fadeUp}>
            Connect, Discover, and Thrive in the Academic Community
          </motion.h1>

          {/* PLACEHOLDER: Hero subtitle */}
          <motion.p className={styles["hero__subtitle"]} variants={fadeUp}>
            The comprehensive academic intelligence platform for researchers,
            institutions, and organizations. Explore U.S. higher education data,
            faculty profiles, publications, and research grants — all in one place.
          </motion.p>

          <motion.div className={styles["hero__actions"]} variants={fadeUp}>
            <a
              href="https://www.cazoodle.com/academic-online/"
              className={styles["hero__btn-primary"]}
              id="hero-explore-more-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Explore more
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 2: INTRO VIDEO
          ════════════════════════════════════════════════════════ */}
      <section className={styles["video-section"]} id="video">
        <motion.div
          className={styles["video-section__container"]}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 className={styles["section-title"]} variants={fadeUp}>
            See Academic Online in Action
          </motion.h2>
          <motion.p className={styles["section-subtitle"]} variants={fadeUp}>
            Watch how our platform empowers researchers and institutions to
            connect, discover, and thrive.
          </motion.p>
          <motion.div className={styles["video-section__player-wrapper"]} variants={fadeUp}>
            {/* PLACEHOLDER: Video URL — Replace with your S3 video link */}
            <ReactPlayer
              url="https://media.academic.online/product/AOLIntroVideo_v6.mp4"
              className={styles["video-section__player"]}
              width="100%"
              height="100%"
              controls
              playing={isPlaying}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <div 
                className={styles["video-section__overlay"]}
                onClick={() => setIsPlaying(true)}
              >
                <div className={styles["video-section__play-btn"]}>
                  <PlayIcon />
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 3: PRODUCTS OVERVIEW
          ════════════════════════════════════════════════════════ */}
      <section className={styles["products"]} id="products">
        <motion.div
          className={styles["products__container"]}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 className={styles["section-title"]} variants={fadeUp}>
            Our Products
          </motion.h2>
          <motion.p className={styles["section-subtitle"]} variants={fadeUp}>
            Two powerful platforms designed to serve the academic community.
          </motion.p>

          <div className={styles["products__grid"]}>
            {/* ── Explore Card ── */}
            <motion.article className={styles["product-card"]} variants={fadeUp}>
              <div className={styles["product-card__image-wrapper"]}>
                {/* REPLACE: Explore screenshot — Put your public S3 link here */}
                <img
                  src="https://media.academic.online/product/Explore_screenshot.png"
                  alt="Academic Online Explore — institution analytics dashboard showing research metrics and faculty data"
                  className={styles["product-card__image"]}
                  loading="lazy"
                />
                <div className={styles["product-card__image-overlay"]}>
                  <span className={styles["product-card__badge-explore"]}>Explore</span>
                </div>
              </div>
              <div className={styles["product-card__body"]}>
                <h3 className={styles["product-card__title"]}>
                  <span className={styles["product-card__icon-explore"]}>
                    <SearchIcon />
                  </span>
                  Explore
                </h3>
                {/* PLACEHOLDER: Explore description */}
                <p className={styles["product-card__description"]}>
                  Tailored for the U.S. academic market, Academic Explore empowers
                   individual researchers to discover and engage with comprehensive
                  academic resources across all fields.
                </p>
                <ul className={styles["product-card__features"]}>
                  {EXPLORE_FEATURES.map((feature, index) => (
                    <li key={index} className={styles["product-card__feature"]}>
                      <span className={styles["product-card__feature-icon"]}>{feature.icon}</span>
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/explore"
                  className={styles["product-card__cta-explore"]}
                  id="product-explore-btn"
                >
                  Explore Now
                  <ArrowRightIcon />
                </a>
              </div>
            </motion.article>

            {/* ── Public Profile Card ── */}
            <motion.article className={styles["product-card"]} variants={fadeUp}>
              <div className={styles["product-card__image-wrapper"]}>
                {/* REPLACE: Public Profile screenshot — Put your public S3 link here */}
                <img
                  src="https://media.academic.online/product/PP_screenshot.png"
                  alt="Academic Online Public Profile — university profile page with faculty directory, publication metrics, and research highlights"
                  className={styles["product-card__image"]}
                  loading="lazy"
                />
                <div className={styles["product-card__image-overlay"]}>
                  <span className={styles["product-card__badge-profile"]}>Public Profile</span>
                </div>
              </div>
              <div className={styles["product-card__body"]}>
                <h3 className={styles["product-card__title"]}>
                  <span className={styles["product-card__icon-profile"]}>
                    <BuildingIcon />
                  </span>
                  Public Profile
                </h3>
                {/* PLACEHOLDER: Public Profile description */}
                <p className={styles["product-card__description"]}>
                  A comprehensive business solution designed for those who seeking to 
                  enhance their academic presence and networking capabilities with 
                  customizable features and partnership opportunities.
                </p>
                <ul className={styles["product-card__features"]}>
                  {PUBLIC_PROFILE_FEATURES.map((feature, index) => (
                    <li key={index} className={styles["product-card__feature"]}>
                      <span className={styles["product-card__feature-icon"]}>{feature.icon}</span>
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://public-profile.academic.online"
                  className={styles["product-card__cta-profile"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="product-public-profile-btn"
                >
                  Learn More
                  <ArrowRightIcon />
                </a>
              </div>
            </motion.article>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Homepage;
